import React, { useEffect, useState, lazy, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { LayoutTemplateIcon, ShieldCheckIcon, SparklesIcon, ArrowRightIcon, ZapIcon, TrendingUpIcon, DatabaseIcon, FileTextIcon, FileJsonIcon, FileCheckIcon, PhoneIcon, BotIcon, MailIcon, CheckCircleIcon, MicIcon, ShieldAlertIcon } from 'lucide-react';
// Shared Components
import AnnouncementBar from '../components/AnnouncementBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Pricing from '../components/Pricing';
import FAQ from '../components/FAQ';
import Interfaces from '../components/Interfaces';
import Integrations from '../components/Integrations';
import PoweredByFluidWeb from '../components/PoweredByFluidWeb';
import AdvancedCapabilities from '../components/AdvancedCapabilities';
import { MultiModalAnimation } from '../components/MultiModalAnimation';
const templates = [{
  name: 'KYC Onboarding',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/kyc-onboarding'
}, {
  name: 'Insurance Claim',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/insurance-claim'
}, {
  name: 'Car Loan',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/car-loan'
}, {
  name: 'Mortgage Application',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/mortgage-application'
}, {
  name: 'Rental Application',
  url: 'https://app.fluid-form.ai/templates/org_38dbPMpQEgbwJMUEGbUS2WVYhnz/rental-application'
}];
const verticalBFaqs = [{
  question: 'What is FluidForm?',
  answer: 'FluidForm is an AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.'
}, {
  question: 'How is this different from a chatbot?',
  answer: "Chat UIs can talk, but they're unreliable at producing complete, schema-valid submissions. FluidForm is schema-first: conversation is used to complete and validate a form — then you get clean structured output."
}, {
  question: 'How reliable is the AI?',
  answer: 'FluidForm is designed for reliability through schema constraints, validation, and confirmations. You control when AI can auto-fill vs. when it must ask or confirm.'
}, {
  question: 'Can I customize the look and feel?',
  answer: 'Yes. Use styling controls or custom CSS. Keep full branding and UX control.'
}, {
  question: 'Is FluidForm right for regulated industries like finance, insurance, and real estate?',
  answer: 'Yes. FluidForm is built for workflows where accuracy, completeness, and auditability matter. Every submission is validated before it reaches your team — so you spend less time chasing applicants and cleaning data.'
}, {
  question: 'How does FluidForm handle sensitive documents like IDs and financial statements?',
  answer: 'FluidForm extracts the information you need from uploaded documents without storing sensitive files beyond your configuration. You control what gets captured and where it goes.'
}, {
  question: 'Can FluidForm connect to our existing tools?',
  answer: 'Yes. FluidForm integrates with your existing stack. Custom integrations are available on Pro and Enterprise plans.'
}];
const verticalBCapabilities = [{
  icon: <FileCheckIcon size={28} className="text-orange-500" data-id="element-660" />,
  title: 'AI Document Verification',
  description: 'Cross-check what applicants tell you against what their documents show:',
  bullets: ['Spot mismatches between form answers and uploaded documents', 'Flag missing or inconsistent evidence automatically', 'Reduce manual review time significantly']
}, {
  icon: <PhoneIcon size={28} className="text-orange-500" data-id="element-661" />,
  title: 'Voice & Call-Based Form Experiences',
  description: 'Let applicants complete forms by speaking:',
  bullets: ['The form guides them with questions', 'They respond naturally out loud', 'AI captures and validates everything — no typing required']
}, {
  icon: <BotIcon size={28} className="text-orange-500" data-id="element-662" />,
  title: 'Guided Sales & Intake Agents',
  description: 'AI that walks applicants through your process while keeping data clean:',
  bullets: ['Handles questions and hesitations in real time', 'Keeps applicants moving — fewer drop-offs', 'Built for revenue and compliance workflows']
}, {
  icon: <MailIcon size={28} className="text-orange-500" data-id="element-663" />,
  title: 'Email & Document Intake',
  description: 'Turn inbound documents into usable data — no manual processing:',
  bullets: ['Applicants email documents directly into your intake flow', 'AI extracts, validates, and summarizes the contents', 'Ideal for ops, legal, and accounting teams']
}];
const VerticalHero = () => {
  const [prompt, setPrompt] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || prompt.length > 0;
  const handleGenerate = () => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    window.open(`https://app.fluid-form.ai/forms?p=${encodeURIComponent(trimmed)}`, '_blank', 'noopener,noreferrer');
  };
  return <section className="relative w-full bg-gradient-to-b from-white via-orange-50/30 to-white pt-[4.75rem] pb-10 overflow-hidden" data-id="element-664">
      <div className="container mx-auto px-4 relative z-10" data-id="element-665">
        <div className="max-w-4xl mx-auto text-center" data-id="element-666">
          <motion.div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200/60 rounded-full px-3 py-1.5 mb-4 sm:mb-6 md:mb-8" initial={{
          opacity: 0,
          y: 12,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-667">
            <ShieldCheckIcon size={14} className="text-orange-500" data-id="element-668" />
            <span className="text-xs sm:text-sm font-medium text-orange-700 tracking-tight" data-id="element-669">
              Built for finance, insurance & real estate
            </span>
          </motion.div>

          <motion.h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6 md:mb-8 tracking-tight px-2" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-670">
            Loan apps, claims & leases. <br className="hidden sm:block" data-id="element-671" />
            High-stakes forms,{' '}
            <span className="text-orange-500" data-id="element-672">zero drop-off.</span>
          </motion.h1>

          <motion.p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-2" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: 'easeOut'
        }} data-id="element-673">
            Let applicants respond naturally with text, voice, and documents —
            and get complete, accurate submissions every time.
          </motion.p>

          <motion.div className="max-w-3xl mx-auto" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-674">
            <div className="relative" data-id="element-675">
              <motion.div className="absolute -inset-3 rounded-3xl" animate={isActive ? {
              background: 'radial-gradient(ellipse at center, rgba(251,146,60,0.15) 0%, rgba(251,146,60,0.05) 50%, transparent 70%)',
              scale: 1.02
            } : {
              background: 'radial-gradient(ellipse at center, rgba(251,146,60,0.08) 0%, rgba(251,146,60,0.02) 50%, transparent 70%)',
              scale: 1
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-676" />

              <motion.div className="absolute -inset-[2px] rounded-2xl border-2 border-orange-300/0 pointer-events-none" animate={isActive ? {
              borderColor: 'rgba(251,146,60,0.3)',
              opacity: [0.5, 1, 0.5]
            } : {
              borderColor: 'rgba(251,146,60,0)',
              opacity: 1
            }} transition={isActive ? {
              borderColor: {
                duration: 0.3
              },
              opacity: {
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            } : {
              duration: 0.3
            }} data-id="element-677" />

              <motion.div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" animate={isActive ? {
              boxShadow: '0 20px 60px -12px rgba(251,146,60,0.2), 0 8px 20px -8px rgba(0,0,0,0.08)',
              borderColor: '#fdba74'
            } : {
              boxShadow: '0 10px 30px -8px rgba(0,0,0,0.08), 0 4px 10px -4px rgba(0,0,0,0.04)',
              borderColor: '#e5e7eb'
            }} whileHover={!isActive ? {
              boxShadow: '0 16px 48px -10px rgba(251,146,60,0.12), 0 6px 16px -6px rgba(0,0,0,0.06)',
              borderColor: '#fed7aa'
            } : {}} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-678">
                <div className="p-4 sm:p-6 md:p-8" data-id="element-679">
                  <div className="flex items-center gap-2 sm:gap-2.5 mb-4 sm:mb-5" data-id="element-680">
                    <motion.div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg" animate={isActive ? {
                    rotate: [0, 15, -10, 5, 0],
                    scale: [1, 1.15, 1]
                  } : {}} transition={{
                    duration: 0.6,
                    ease: 'easeOut'
                  }} data-id="element-681">
                      <SparklesIcon size={18} className="text-orange-500 sm:w-5 sm:h-5" data-id="element-682" />
                    </motion.div>
                    <span className="text-base sm:text-lg font-bold text-gray-900" data-id="element-683">
                      Describe your application or intake form
                    </span>
                  </div>

                  <div className="w-full" data-id="element-684">
                    <motion.div animate={isFocused ? {
                    boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.1)',
                    borderColor: '#fb923c'
                  } : {
                    boxShadow: '0 0 0 0px rgba(249, 115, 22, 0)',
                    borderColor: '#e5e7eb'
                  }} transition={{
                    duration: 0.25
                  }} className="w-full rounded-xl border bg-white overflow-hidden relative" data-id="element-685">
                      <textarea value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="Describe your form (e.g., create a mortgage application form)" rows={2} className="w-full px-3 sm:px-4 pt-3 sm:pt-3.5 pb-12 sm:pb-14 text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:outline-none resize-none" data-id="element-686" />
                      <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3" data-id="element-687">
                        <motion.button onClick={handleGenerate} disabled={!prompt.trim()} className="inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-orange-500 text-white pl-3 sm:pl-4 pr-2.5 sm:pr-3.5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:bg-orange-600 transition-all shadow-sm hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-orange-500 disabled:hover:shadow-sm min-h-[44px]" whileHover={prompt.trim() ? {
                        scale: 1.03
                      } : {}} whileTap={prompt.trim() ? {
                        scale: 0.97
                      } : {}} animate={prompt.trim() ? {
                        opacity: 1
                      } : {
                        opacity: 0.5
                      }} transition={{
                        duration: 0.2
                      }} data-id="element-688">
                          <SparklesIcon size={14} className="sm:w-[15px] sm:h-[15px]" data-id="element-689" />
                          <span className="hidden xs:inline" data-id="element-690">
                            Generate Form
                          </span>
                          <span className="xs:hidden" data-id="element-691">Generate</span>
                          <ArrowRightIcon size={14} className="sm:w-[15px] sm:h-[15px]" data-id="element-692" />
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                </div>

                <div className="bg-gray-50/50 px-4 sm:px-6 md:px-8 py-4 sm:py-5 border-t border-gray-100" data-id="element-693">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4" data-id="element-694">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap" data-id="element-695">
                      <LayoutTemplateIcon size={14} className="sm:w-4 sm:h-4" data-id="element-696" />
                      <span data-id="element-697">Or start with a template:</span>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-698">
                      {templates.map((template, index) => <motion.a key={template.name} href={template.url} target="_blank" rel="noopener noreferrer" className="px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 hover:border-orange-300 rounded-full text-xs sm:text-sm font-semibold text-orange-700 hover:text-orange-800 transition-all shadow-sm hover:shadow min-h-[44px] flex items-center" initial={{
                      opacity: 0,
                      y: 8
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.5 + index * 0.07,
                      duration: 0.35,
                      ease: 'easeOut'
                    }} whileHover={{
                      scale: 1.06,
                      y: -1
                    }} whileTap={{
                      scale: 0.97
                    }} data-id="element-699">
                          {template.name}
                        </motion.a>)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4" data-id="element-700">
              No credit card required • Free to start
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
};
const VerticalMetrics = () => {
  const metrics = [{
    icon: <ZapIcon size={28} className="text-orange-500" data-id="element-701" />,
    percentage: 'Up to 73%',
    suffix: 'fewer',
    description: 'applicants dropping off mid-form'
  }, {
    icon: <TrendingUpIcon size={28} className="text-orange-500" data-id="element-702" />,
    percentage: 'Up to 4x',
    suffix: 'faster',
    description: 'application completion'
  }, {
    icon: <ShieldCheckIcon size={28} className="text-orange-500" data-id="element-703" />,
    percentage: '90%+',
    suffix: 'accuracy',
    description: 'on data collected from documents'
  }];
  return <section className="w-full bg-gray-50 border-t border-gray-200 py-[3.8rem]" data-id="element-704">
      <div className="container mx-auto px-4" data-id="element-705">
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-706">
          {metrics.map((metric, index) => <div key={index} className="flex flex-col items-center text-center p-6" data-id="element-707">
              <div className="bg-orange-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6" data-id="element-708">
                {metric.icon}
              </div>
              <div className="flex items-baseline gap-2 mb-3" data-id="element-709">
                <span className="text-5xl font-bold text-gray-900" data-id="element-710">
                  {metric.percentage}
                </span>
                <span className="text-xl font-semibold text-orange-500" data-id="element-711">
                  {metric.suffix}
                </span>
              </div>
              <p className="text-gray-600 leading-relaxed max-w-xs" data-id="element-712">
                {metric.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
const VerticalWhyItWorks = () => {
  const reasons = [{
    icon: <ShieldAlertIcon className="text-orange-500" size={24} data-id="element-713" />,
    title: 'Catches bad data before it becomes your problem',
    description: 'AI validates every submission — missing fields, inconsistencies, and suspicious entries get flagged before they hit your systems'
  }, {
    icon: <FileTextIcon className="text-orange-500" size={24} data-id="element-714" />,
    title: 'Documents accepted as answers',
    description: 'IDs, bank statements, proof of address — users upload once, AI pulls the information you need automatically'
  }, {
    icon: <CheckCircleIcon className="text-orange-500" size={24} data-id="element-715" />,
    title: 'Applicants confirm what matters',
    description: 'Critical fields get explicit confirmation before submission — fewer mistakes, less back-and-forth'
  }, {
    icon: <DatabaseIcon className="text-orange-500" size={24} data-id="element-716" />,
    title: 'Clean data, every time',
    description: 'Every submission comes through complete and consistent — ready for your team to act on, not clean up'
  }];
  return <section className="w-full bg-gray-50 py-12 sm:py-16 md:py-20 lg:py-[4.75rem] border-y border-gray-200" data-id="element-717">
      <div className="container mx-auto px-4" data-id="element-718">
        <div className="max-w-4xl mx-auto" data-id="element-719">
          <div className="text-center mb-10 sm:mb-12" data-id="element-720">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-2" data-id="element-721">
              Why teams in finance, insurance & real estate choose FluidForm
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed px-2" data-id="element-722">
              Set up in minutes. No engineering team required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-723">
            {reasons.map((reason, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-5 sm:p-6 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3 sm:gap-4" data-id="element-724">
                <div className="bg-orange-50 p-2.5 sm:p-3 rounded-lg shrink-0" data-id="element-725">
                  {reason.icon}
                </div>
                <div data-id="element-726">
                  <h3 className="font-bold text-gray-900 mb-1 text-base sm:text-lg" data-id="element-727">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-728">
                    {reason.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
};
const FeatureCard = ({
  icon,
  title,
  description,
  bullets,
  controlLine,
  imageUrl,
  customComponent,
  index
}: any) => {
  const isEven = index % 2 === 0;
  return <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 sm:gap-8 lg:gap-12 items-center`} data-id="element-729">
      <div className="flex-1 w-full" data-id="element-730">
        <div className="bg-white rounded-2xl p-5 sm:p-8 border border-gray-200 shadow-sm" data-id="element-731">
          <div className="bg-orange-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6" data-id="element-732">
            {icon}
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4" data-id="element-733">
            {title}
          </h3>
          <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed" data-id="element-734">
            {description}
          </p>
          <ul className="space-y-2 sm:space-y-3" data-id="element-735">
            {bullets.map((bullet: string, idx: number) => <li key={idx} className="flex items-start gap-3" data-id="element-736">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" data-id="element-737"></div>
                <span className="text-sm sm:text-base text-gray-700" data-id="element-738">
                  {bullet}
                </span>
              </li>)}
          </ul>
          {controlLine && <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 text-xs sm:text-sm text-gray-500 italic" data-id="element-739">
              {controlLine}
            </div>}
        </div>
      </div>
      <div className="flex-1 w-full" data-id="element-740">
        <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg" data-id="element-741">
          {customComponent ? customComponent : imageUrl ? <img src={imageUrl} alt={title} className="w-full h-auto" loading="lazy" width="800" height="600" data-id="element-742" /> : <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center" data-id="element-743">
              <p className="text-gray-400" data-id="element-744">Feature visualization</p>
            </div>}
        </div>
      </div>
    </div>;
};
const VerticalFeatures = () => {
  const features = [{
    icon: <SparklesIcon size={24} className="text-orange-500" data-id="element-745" />,
    title: 'Turn Any Document Into a Structured Form',
    description: 'Build your form in minutes:',
    bullets: ['Generate forms from voice, screenshots, or existing PDFs', 'Convert onboarding documents into ready-to-use forms', 'Automatically style to match your brand'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/3EGqFrdu512DGNteqCHWy5/AIFormCreationGIF.gif'
  }, {
    icon: <MicIcon size={24} className="text-orange-500" data-id="element-746" />,
    title: 'Accept Documents, Photos & Voice — In a Single Form',
    description: 'Meet applicants where they are:',
    bullets: ['Type or speak responses', 'Upload documents or images', 'Capture photos directly from their camera', 'Everything maps to your form fields automatically'],
    customComponent: <MultiModalAnimation data-id="element-747" />
  }, {
    icon: <FileTextIcon size={24} className="text-orange-500" data-id="element-748" />,
    title: 'Auto-Extract From Any Document',
    description: "Stop asking applicants to re-enter what's already in their documents.",
    bullets: ['Upload PDFs, scans, images, or handwritten forms', 'AI pulls the information you need automatically', 'Faster review, fewer errors, no manual data entry'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/pQbNfY5D7CH7kKfiWK7TSZ/pdffFileInputExtraction.png'
  }, {
    icon: <ShieldAlertIcon size={24} className="text-orange-500" data-id="element-749" />,
    title: 'AI Validation That Catches What Rules Miss',
    description: 'More than required fields — validation that protects your data.',
    bullets: ['Catch missing, inconsistent, or suspicious entries', 'Ask smart follow-up questions only when needed'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/tz8BCjnR7vnjcmCref1oK5/AIValidationDateofBirth.png'
  }];
  return <section className="w-full bg-gradient-to-b from-white to-gray-50 py-[2.85rem] sm:py-[3.8rem] lg:py-[5.75rem]" id="features" data-id="element-750">
      <div className="container mx-auto px-4" data-id="element-751">
        <div className="text-center mb-10 sm:mb-16 lg:mb-20" data-id="element-752">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-753">
            Features Built for High-Stakes Data Collection
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-754">
            More completions, better data, less manual work — without changing
            your existing workflow.
          </p>
        </div>
        <div className="space-y-12 sm:space-y-16 lg:space-y-24 max-w-7xl mx-auto" data-id="element-755">
          {features.map((feature, index) => <FeatureCard key={index} {...feature} index={index} data-id="element-756" />)}
        </div>
      </div>
    </section>;
};
const VerticalCTA = () => {
  return <section className="w-full bg-gradient-to-br from-orange-400 via-orange-500 to-amber-500 py-12 sm:py-16 md:py-20 lg:py-[5.75rem] relative overflow-hidden" data-id="element-757">
      <div className="absolute inset-0 opacity-[0.06]" data-id="element-758">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-759"></div>
      </div>

      <div className="container mx-auto px-4 text-center relative z-10" data-id="element-760">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 tracking-tight px-2" data-id="element-761">
          Stop losing applicants to your own forms.
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-762">
          Start free today. See why finance, insurance, and real estate teams
          use FluidForm to collect better data with less effort.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-763">
          <a href="https://accounts.fluid-form.ai/sign-up" target="_blank" onClick={() => window.gtag('event', 'signup_click', {
          source: 'nav_login'
        })} rel="noopener noreferrer" className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-orange-600 rounded-lg font-bold text-base sm:text-lg hover:bg-gray-50 transition-all shadow-xl w-full sm:w-auto min-h-[44px] flex items-center justify-center" data-id="element-764">
            Try it now
          </a>
        </div>

        <p className="text-white/70 mt-4 sm:mt-6 text-xs sm:text-sm" data-id="element-765">
          No credit card required.
        </p>
      </div>
    </section>;
};
export default function VerticalB() {
  useEffect(() => {
    // ── Page title ──
    const prevTitle = document.title;
    document.title = 'FluidForm — Loan Apps, Claims & Leases. High-Stakes Forms, Zero Drop-Off.';
    // ── Helper to set/create meta tags ──
    function setMeta(attr: string, value: string, content: string) {
      let el = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    }
    // ── Meta description ──
    const metaDesc = setMeta('name', 'description', 'AI-powered forms for loan applications, insurance claims, and leases. Let applicants respond with text, voice, and documents — get complete, accurate submissions every time. Built for finance, insurance & real estate.');
    // ── Canonical URL ──
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://fluid-form.ai/b';
    // ── Open Graph tags ──
    const ogTitle = setMeta('property', 'og:title', 'FluidForm — Loan Apps, Claims & Leases. High-Stakes Forms, Zero Drop-Off.');
    const ogDesc = setMeta('property', 'og:description', 'AI-powered forms for loan applications, insurance claims, and leases. Let applicants respond with text, voice, and documents — get complete, accurate submissions every time.');
    const ogUrl = setMeta('property', 'og:url', 'https://fluid-form.ai/b');
    const ogType = setMeta('property', 'og:type', 'website');
    const ogSiteName = setMeta('property', 'og:site_name', 'FluidForm');
    const ogImage = setMeta('property', 'og:image', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2KuE4GiXagr4eVmupSGvce/fluid-form-logo.png');
    const ogImageAlt = setMeta('property', 'og:image:alt', 'FluidForm — AI-powered forms for finance, insurance & real estate');
    // ── Twitter Card tags ──
    const twCard = setMeta('name', 'twitter:card', 'summary_large_image');
    const twTitle = setMeta('name', 'twitter:title', 'FluidForm — Loan Apps, Claims & Leases. High-Stakes Forms, Zero Drop-Off.');
    const twDesc = setMeta('name', 'twitter:description', 'AI-powered forms for finance, insurance & real estate. Let applicants respond with text, voice, and documents. Complete, accurate submissions every time.');
    const twImage = setMeta('name', 'twitter:image', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2KuE4GiXagr4eVmupSGvce/fluid-form-logo.png');
    const twSite = setMeta('name', 'twitter:site', '@fluidform');
    // ── Robots ──
    const robots = setMeta('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large');
    // ── JSON-LD Structured Data ──
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'WebPage',
        '@id': 'https://fluid-form.ai/b',
        url: 'https://fluid-form.ai/b',
        name: 'FluidForm — Loan Apps, Claims & Leases. High-Stakes Forms, Zero Drop-Off.',
        description: 'AI-powered forms for loan applications, insurance claims, and leases. Let applicants respond with text, voice, and documents — get complete, accurate submissions every time. Built for finance, insurance & real estate.',
        isPartOf: {
          '@id': 'https://fluid-form.ai/#website'
        },
        breadcrumb: {
          '@id': 'https://fluid-form.ai/b/#breadcrumb'
        }
      }, {
        '@type': 'BreadcrumbList',
        '@id': 'https://fluid-form.ai/b/#breadcrumb',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://fluid-form.ai'
        }, {
          '@type': 'ListItem',
          position: 2,
          name: 'Finance, Insurance & Real Estate Forms',
          item: 'https://fluid-form.ai/b'
        }]
      }, {
        '@type': 'SoftwareApplication',
        name: 'FluidForm',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://fluid-form.ai/b',
        description: 'AI-native form engine built for finance, insurance & real estate. Captures structured data from text, voice, and document inputs with schema-enforced validation and audit-ready outputs.',
        offers: [{
          '@type': 'Offer',
          name: 'Free Plan',
          price: '0',
          priceCurrency: 'USD',
          description: '1 user, 3 forms, 25 submissions/month, AI form builder'
        }, {
          '@type': 'Offer',
          name: 'Pro Plan',
          price: '39',
          priceCurrency: 'USD',
          description: '3 users, 10 forms, 1,000 submissions/month, advanced AI validations'
        }],
        featureList: ['Turn Any Document Into a Structured Form', 'Accept Documents, Photos & Voice in a Single Form', 'Auto-Extract From Any Document', 'AI Validation That Catches What Rules Miss', 'AI Document Verification', 'Voice & Call-Based Form Experiences', 'Guided Sales & Intake Agents', 'Email & Document Intake', 'Multi-Modal Inputs (text, voice, documents, photos)', 'KYC Onboarding Templates', 'Insurance Claim Forms', 'Car Loan Forms', 'Mortgage Application Forms', 'Rental Application Forms']
      }, {
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: 'What is FluidForm?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm is an AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.'
          }
        }, {
          '@type': 'Question',
          name: 'How is this different from a chatbot?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Chat UIs can talk, but they're unreliable at producing complete, schema-valid submissions. FluidForm is schema-first: conversation is used to complete and validate a form — then you get clean structured output."
          }
        }, {
          '@type': 'Question',
          name: 'How reliable is the AI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm is designed for reliability through schema constraints, validation, and confirmations. You control when AI can auto-fill vs. when it must ask or confirm.'
          }
        }, {
          '@type': 'Question',
          name: 'Can I customize the look and feel?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Use styling controls or custom CSS. Keep full branding and UX control.'
          }
        }, {
          '@type': 'Question',
          name: 'Is FluidForm right for regulated industries like finance, insurance, and real estate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. FluidForm is built for workflows where accuracy, completeness, and auditability matter. Every submission is validated before it reaches your team — so you spend less time chasing applicants and cleaning data.'
          }
        }, {
          '@type': 'Question',
          name: 'How does FluidForm handle sensitive documents like IDs and financial statements?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm extracts the information you need from uploaded documents without storing sensitive files beyond your configuration. You control what gets captured and where it goes.'
          }
        }, {
          '@type': 'Question',
          name: 'Can FluidForm connect to our existing tools?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. FluidForm integrates with your existing stack. Custom integrations are available on Pro and Enterprise plans.'
          }
        }]
      }, {
        '@type': 'Organization',
        '@id': 'https://fluid-form.ai/#organization',
        name: 'FluidForm, Inc.',
        url: 'https://fluid-form.ai',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@fluid-form.ai',
          contactType: 'customer support'
        }
      }]
    };
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'vertical-b-jsonld';
    ldScript.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(ldScript);
    // ── Cleanup on unmount ──
    return () => {
      document.title = prevTitle;
      const ld = document.getElementById('vertical-b-jsonld');
      if (ld) ld.remove();
      if (canonical) canonical.href = 'https://fluid-form.ai';
    };
  }, []);
  return <div className="w-full min-h-screen font-sans" data-id="element-766">
      <div className="sticky top-0 z-50" data-id="element-767">
        <AnnouncementBar data-id="element-768" />
        <Navbar basePath="/b" data-id="element-769" />
      </div>
      <main data-id="element-770">
        <VerticalHero data-id="element-771" />
        <VerticalMetrics data-id="element-772" />
        <VerticalWhyItWorks data-id="element-773" />
        <VerticalFeatures data-id="element-774" />
        <Interfaces data-id="element-775" />
        <Integrations data-id="element-776" />
        <PoweredByFluidWeb data-id="element-777" />
        <AdvancedCapabilities headline="Do More With Your Onboarding Stack" subheadline="For high-volume teams or high-touch workflows — add powerful capabilities without rebuilding how you collect data." capabilities={verticalBCapabilities} data-id="element-778" />
        <Pricing data-id="element-779" />
        <FAQ faqs={verticalBFaqs} data-id="element-780" />
        <VerticalCTA data-id="element-781" />
      </main>
      <Footer basePath="/b" data-id="element-782" />
    </div>;
}